import React from "react";
import {
    Checkbox,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography
} from "@mui/material";
import { AddCircle, Refresh, Settings } from "@mui/icons-material";
import { headCells, getComparator, stableSort } from "./pluginTableUtils";

const PluginsTableHead = ({ order, orderBy, onRequestSort }) => {
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell />
                <TableCell />
            </TableRow>
        </TableHead>
    );
};

const PluginsToolbar = ({ onAddPlugin }) => (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
        <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
            Плагины
        </Typography>
        <IconButton onClick={onAddPlugin}>
            <AddCircle color="primary" fontSize="large" style={{ cursor: 'pointer' }} />
        </IconButton>
    </Toolbar>
);

const PluginsTable = ({
    plugins,
    order,
    orderBy,
    page,
    rowsPerPage,
    dense,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onAddPlugin,
    onOpenDetails,
    onToggleStatus,
    onOpenSettings,
    onUpdatePlugin
}) => {
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - plugins.length) : 0;
    const visibleRows = React.useMemo(
        () => stableSort(plugins, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage, plugins],
    );

    return (
        <Paper sx={{ width: '100%', mb: 2 }}>
            <PluginsToolbar onAddPlugin={onAddPlugin} />
            <TableContainer>
                <Table sx={{ minWidth: 550 }} aria-labelledby="tableTitle" size={dense ? 'small' : 'medium'}>
                    <PluginsTableHead order={order} orderBy={orderBy} onRequestSort={onRequestSort} />
                    <TableBody>
                        {plugins && visibleRows.map((row, index) => {
                            const labelId = `enhanced-table-checkbox-${index}`;
                            return (
                                <TableRow hover role="checkbox" tabIndex={-1} key={row.name}>
                                    <TableCell component="th" id={labelId} scope="row" padding="none" align="right">
                                        {row.plugin_id}
                                    </TableCell>
                                    <TableCell align="left" style={{ cursor: 'pointer' }} onClick={() => onOpenDetails(row.plugin_id)}>{row.name}</TableCell>
                                    <TableCell align="left">{row.provider}</TableCell>
                                    <TableCell align="left">{row.version}</TableCell>
                                    <TableCell align="left">{row.download_url}</TableCell>
                                    <TableCell align="left">{row.updated_at}</TableCell>
                                    <TableCell align="left">
                                        <Checkbox checked={Boolean(row.status)} onChange={() => onToggleStatus(!row.status, row.plugin_id)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Settings color="action" style={{ cursor: 'pointer' }} onClick={() => onOpenSettings(row)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Refresh color="action" style={{ cursor: 'pointer' }} onClick={() => onUpdatePlugin(row.plugin_id)} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[9, 18, 36]}
                labelRowsPerPage="Записей на странице"
                component="div"
                count={plugins.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
            />
        </Paper>
    );
};

export default PluginsTable;
